import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import SlipPowerModel from './models/SlipPowerModel';
import MotorRenderer from './visualization/MotorRenderer';
import ControlPanel from './components/ControlPanel';
import DataVisualizer from './components/DataVisualizer';
import AIAdvisor from './components/AIAdvisor';
import ReportGenerator from './components/ReportGenerator';
import QuizMode from './components/QuizMode';
import { FaVolumeUp, FaVolumeMute, FaQuestion, FaLightbulb, FaRobot, FaDownload, FaGraduationCap, FaVrCardboard } from 'react-icons/fa';

function App() {
  const [model] = useState(new SlipPowerModel());
  const [simulationData, setSimulationData] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  
  // Refs
  const motorContainerRef = useRef(null);
  const motorRendererRef = useRef(null);
  
  // UI States
  const [glassTheme, setGlassTheme] = useState(true);
  const [voiceAssistant, setVoiceAssistant] = useState(false);
  const [arMode, setArMode] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [aiAdvisor, setAiAdvisor] = useState(false);
  const [showQuizMode, setShowQuizMode] = useState(false);
  
  // Historical data for charts and reports
  const [historicalData, setHistoricalData] = useState({
    timestamps: [],
    slip: [],
    efficiency: [],
    rotorPower: [],
    recoveredPower: [],
    rotorVoltage: [],
    rotorCurrent: []
  });
  
  // Check AR support function - moved inside component
  const checkARSupport = () => {
    if (navigator.xr) {
      navigator.xr.isSessionSupported('immersive-ar')
        .then(supported => {
          // Only enable AR button if supported
          if (!supported) {
            setArMode(false);
          }
        })
        .catch(() => {
          setArMode(false);
        });
    } else {
      setArMode(false);
    }
  };
  
  // AR support check effect - moved inside component
  useEffect(() => {
    checkARSupport();
  }, []);
  
  // Initialize motor renderer
  useEffect(() => {
    if (motorContainerRef.current && !motorRendererRef.current) {
      motorRendererRef.current = new MotorRenderer(motorContainerRef.current);
    }
    
    // Start simulation loop
    const simulationInterval = setInterval(() => {
      const data = model.update(0.1); // Update with 100ms time step
      setSimulationData(data);
      
      // Update motor visualization
      if (motorRendererRef.current) {
        motorRendererRef.current.updateMotor(data, model.driveType);
      }
      
      // Update historical data
      updateHistoricalData(data);
      
    }, 100);
    
    return () => clearInterval(simulationInterval);
  }, [model]);
  
  // Update historical data for charts and reports
  const updateHistoricalData = (data) => {
    setHistoricalData(prev => {
      const newData = { ...prev };
      const now = Date.now();
      
      // Limit data points to prevent memory issues
      const maxDataPoints = 100;
      
      newData.timestamps = [...prev.timestamps, now].slice(-maxDataPoints);
      newData.slip = [...prev.slip, data.slip * 100].slice(-maxDataPoints);
      newData.efficiency = [...prev.efficiency, data.efficiency * 100].slice(-maxDataPoints);
      newData.rotorPower = [...prev.rotorPower, data.rotorPower / 1000].slice(-maxDataPoints);
      newData.recoveredPower = [...prev.recoveredPower, data.recoveredPower / 1000].slice(-maxDataPoints);
      newData.rotorVoltage = [...prev.rotorVoltage, data.rotorVoltage].slice(-maxDataPoints);
      newData.rotorCurrent = [...prev.rotorCurrent, data.rotorCurrent].slice(-maxDataPoints);
      
      return newData;
    });
  };
  
  // Handle parameter changes from control panel
  const handleParameterChange = (parameter, value) => {
    switch (parameter) {
      case 'slip':
        model.slip = value;
        model.rotorSpeed = model.syncSpeed * (1 - value);
        break;
      case 'driveType':
        model.driveType = value;
        break;
      case 'controlMode':
        model.controlMode = value;
        break;
      case 'loadTorque':
        model.loadTorque = value;
        break;
      default:
        break;
    }
  };
  
  // Toggle voice assistant
  const toggleVoiceAssistant = () => {
    setVoiceAssistant(!voiceAssistant);
    // Initialize speech synthesis and recognition if enabled
    if (!voiceAssistant) {
      // Code to initialize voice assistant
      console.log("Voice assistant enabled");
    }
  };
  
  // Toggle AR mode
  const toggleARMode = () => {
    setArMode(!arMode);
    if (!arMode && motorRendererRef.current) {
      motorRendererRef.current.enableARMode();
    }
  };
  
  // Toggle comparison mode
  const toggleComparisonMode = () => {
    setComparisonMode(!comparisonMode);
    if (!comparisonMode && motorRendererRef.current) {
      motorRendererRef.current.enableComparisonMode();
    }
  };
  
  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''} ${glassTheme ? 'glass-theme' : ''}`}>
      <header className="app-header glass-panel">
        <h1>⚙️ Slip Power Recovery Simulation</h1>
        <div className="header-controls">
          <button onClick={() => setDarkMode(!darkMode)} className="icon-button">
            {darkMode ? '🌙' : '☀️'}
          </button>
          <button onClick={() => setGlassTheme(!glassTheme)} className="icon-button">
            {glassTheme ? '💎' : '🔲'}
          </button>
          <button onClick={() => setAudioEnabled(!audioEnabled)} className="icon-button">
            {audioEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
          </button>
          <button onClick={() => setShowTutorial(!showTutorial)} className="icon-button">
            <FaQuestion />
          </button>
          <button onClick={toggleVoiceAssistant} className="icon-button">
            {voiceAssistant ? '🎤' : '🎤'}
          </button>
          <button onClick={toggleARMode} className="icon-button">
            <FaVrCardboard />
          </button>
        </div>
      </header>
      
      <div className="main-content">
        <div className="left-panel">
          <ControlPanel 
            model={model} 
            onParameterChange={handleParameterChange} 
          />
          
          <div className="feature-toggles glass-panel">
            <h3>Advanced Features</h3>
            <div className="toggle-buttons">
              <button 
                className={`toggle-btn ${aiAdvisor ? 'active' : ''}`}
                onClick={() => setAiAdvisor(!aiAdvisor)}
              >
                <FaRobot /> AI Advisor
              </button>
              <button 
                className={`toggle-btn ${comparisonMode ? 'active' : ''}`}
                onClick={toggleComparisonMode}
              >
                <FaLightbulb /> Comparison Mode
              </button>
              <button 
                className={`toggle-btn ${showQuizMode ? 'active' : ''}`}
                onClick={() => setShowQuizMode(!showQuizMode)}
              >
                <FaGraduationCap /> Quiz Mode
              </button>
            </div>
          </div>
          
          {aiAdvisor && (
            <AIAdvisor 
              simulationData={simulationData} 
              model={model} 
            />
          )}
          
          {showQuizMode && (
            <QuizMode 
              model={model} 
              simulationData={simulationData} 
            />
          )}
        </div>
        
        <div className="center-panel">
          <div 
            ref={motorContainerRef} 
            className="motor-container glass-panel"
          ></div>
          
          <div className="report-container glass-panel">
            <ReportGenerator 
              simulationData={simulationData} 
              historicalData={historicalData} 
              model={model} 
            />
          </div>
        </div>
        
        <div className="right-panel">
          <DataVisualizer 
            model={model} 
            simulationData={simulationData} 
          />
        </div>
      </div>
      
      {showTutorial && (
        <div className="tutorial-overlay">
          <div className="tutorial-content glass-panel">
            <h2>Welcome to Slip Power Recovery Simulation</h2>
            <p>This simulation demonstrates how slip power recovery works in wound rotor induction motors.</p>
            <h3>Getting Started:</h3>
            <ol>
              <li>Use the <strong>Control Panel</strong> to adjust slip, load, and drive type</li>
              <li>Watch the <strong>3D Motor</strong> to see energy flow visualization</li>
              <li>Monitor performance metrics in the <strong>Data Visualizer</strong></li>
              <li>Enable <strong>AI Advisor</strong> for intelligent suggestions</li>
              <li>Try <strong>Quiz Mode</strong> to test your knowledge</li>
              <li>Use <strong>Comparison Mode</strong> to see Scherbius vs Kramer drives side by side</li>
              <li>Generate reports with the <strong>Report Generator</strong></li>
            </ol>
            <button onClick={() => setShowTutorial(false)} className="close-tutorial-btn">
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;